import React, { useCallback, useEffect, useRef } from 'react';
import NoteTemplatesTableRow from './NoteTemplatesTableRow';
import { NoteTemplate } from '../../../../api/note-templates';
import './NoteTemplatesTable.scss';

interface NoteTemplatesTableProps {
    templatesList: NoteTemplate[];
    onView: (template: NoteTemplate) => void;
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
}

const NoteTemplatesTable: React.FC<NoteTemplatesTableProps> = ({
    templatesList,
    onView,
    hasMore,
    isLoadingMore,
    onLoadMore
}) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollTimeoutRef = useRef<number | null>(null);

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || !hasMore || isLoadingMore) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const threshold = 100;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
        const isContentSmallerThanContainer = scrollHeight <= clientHeight + threshold;

        if (isNearBottom || isContentSmallerThanContainer) {
            onLoadMore();
        }
    }, [hasMore, isLoadingMore, onLoadMore]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const onScroll = () => {
            if (scrollTimeoutRef.current) {
                window.clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = window.setTimeout(() => {
                handleScroll();
            }, 100);
        };

        container.addEventListener('scroll', onScroll, { passive: true });

        
        if (hasMore && templatesList.length > 0) {
            setTimeout(() => handleScroll(), 100);
        }

        return () => {
            container.removeEventListener('scroll', onScroll);
            if (scrollTimeoutRef.current) {
                window.clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [handleScroll, hasMore, templatesList.length]);

    return (
        <div className='note-templates-container'>
            <div className='note-templates-table'>
                <div className='table-header'>
                    <div className='header-cell'>Name of Template</div>
                    <div className='header-cell'>Category</div>
                    {/* <div className='header-cell'>Template</div> */}
                    <div className='header-cell actions'></div>
                </div>
                <div className='table-body' ref={scrollContainerRef}>
                    {templatesList?.length > 0 ? (
                        templatesList.map((template) => (
                            <NoteTemplatesTableRow
                                templateData={template}
                                key={template.id}
                                onView={onView}
                            />
                        ))
                    ) : (
                        <div className='empty-state' style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No note templates found.
                        </div>
                    )}
                    {isLoadingMore && templatesList.length > 0 && (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                            Loading more templates...
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NoteTemplatesTable;
